import React from 'react';
import styled from 'styled-components';

import { mediaQueries } from 'shared/styles';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { UseProductOptionsStateReturn } from 'src/hooks/use-product-options-state';

import { OptionTile } from 'src/components/option-tile';
import { useListPriceTreatment } from 'src/hooks/use-list-price-treatment';

type ProductOptionsV2Props = Pick<UseProductOptionsStateReturn, 'options' | 'selectedOption'> & {
  handleOptionClick: (opt: UseProductOptionsStateReturn['selectedOption']) => void;
};

export const ProductOptionsV2 = ({
  options,
  selectedOption,
  handleOptionClick,
}: ProductOptionsV2Props): JSX.Element => {
  const { dispensary } = useDispensary();
  const { listPriceEnabled, hideStrikethrough, formatStandardPrice } = useListPriceTreatment();

  const hideDiscount = useHideDiscountFromConsumer(dispensary);
  const hasMultipleOptions = options.length > 1;

  return (
    <Wrapper>
      <Options>
        {options.map((option) => (
          <OptionTile
            key={option.value}
            label={option.label}
            currentPrice={option.special ? option.special.formattedPrice : option.formattedPrice}
            originalPrice={option.special ? formatStandardPrice(option.formattedPrice) : null}
            discountLabel={option.special && !hideDiscount ? option.formattedDiscount : null}
            hasMultipleOptions={hasMultipleOptions}
            onClick={() => handleOptionClick(option)}
            isSelected={selectedOption.value === option.value}
            isFixedSize
            listPriceEnabled={listPriceEnabled}
            hideStrikethrough={hideStrikethrough}
          />
        ))}
      </Options>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin: 24px 0;

  @media ${mediaQueries.belowMedium} {
    overflow-x: auto;
    -ms-overflow-style: none;
    scroll-behavior: smooth;
    scrollbar-width: none;

    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Options = styled.div`
  display: flex;
  gap: 10px;

  @media ${mediaQueries.medium} {
    flex-wrap: wrap;
  }
`;
